// widgets/location-popover/ui/LocationPopoverDesktop.tsx

import { useToken } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PopoverRoot, PopoverTrigger } from '@/components/ui/popover';

import type { IPoint } from '@/_store/common/types';
import type { IRedux } from '@/_store/types';

import { onOpenMap } from '@/_store/common/common.slice'; // enable when needed
import { mapDesktopLog } from '@/_components/Map/mapDesktopLog';
import { sessionActions } from '@/_store/session/session.slice';
import { getLocationPopoverState } from '../lib/location-popover-state';
import LocationPopoverContent from './popover-content';
import LocationPopoverTrigger from './popover-trigger';
import LocationTriggerLabel from './trigger-label';
import { useActiveBrand } from '@/_hooks/useActiveBrand';

interface Props {
  open: boolean;
  activePoint: IPoint | null;
  setOpen: (open: boolean) => void;
  openBranches: () => void;
}

const LocationPopoverDesktop: React.FC<Props> = ({
  open,
  activePoint,
  setOpen,
  openBranches,
}) => {
  useTranslations(); // keep intl hook mounted
  const dispatch = useDispatch();

  const { permissions, order_type } = useSelector(
    (state: IRedux) => state.session
  );
  const { branches } = useSelector((state: IRedux) => state.common);
  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]

  const primary = useToken('colors', 'primary');

  const primarySubtle = useMemo(() => {
    const color = primary?.[0] ?? '#000000';
    // ⚠️ works only for #RRGGBB, better move to theme later
    return color.length > 4 ? `${color}14` : `${color}1`;
  }, [primary]);

  const geolocationAccepted = permissions.geolocation === 'accepted';

  const popoverState = useMemo(() => {
    return getLocationPopoverState({
      orderType: order_type,
      geolocation: permissions.geolocation,
      activePoint,
      branch,
    });
  }, [order_type, permissions.geolocation, activePoint, branch]);

  const onClose = () => setOpen(false);

  const onConfirmSaved = () => {
    dispatch(sessionActions.acceptPGL());
  };

  const onOpenSelector = () => {
    mapDesktopLog('1 header-popover-ok', {
      orderType: order_type?.type ?? null,
      popoverMode: popoverState.mode,
    });
    if (order_type?.type === 'self-pickup') {
      mapDesktopLog('2 openBranches (self-pickup)');
      openBranches();
    } else {
      mapDesktopLog('2 dispatch onOpenMap');
      dispatch(onOpenMap());
    }

    mapDesktopLog('3 close location popover');
    setOpen(false);
  };

  return (
    <PopoverRoot
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
    >
      <PopoverTrigger asChild>
        <div>
          <LocationPopoverTrigger
            open={open}
            primarySubtle={primarySubtle}
            label={<LocationTriggerLabel state={popoverState} />}
          />
        </div>
      </PopoverTrigger>

      <LocationPopoverContent
        open={open}
        mode={popoverState.mode}
        geolocationAccepted={geolocationAccepted}
        orderType={order_type}
        activePoint={activePoint}
        branch={branch}
        onClose={onClose}
        onOpenSelector={onOpenSelector}
        onConfirmSaved={onConfirmSaved}
      />
    </PopoverRoot>
  );
};

export default LocationPopoverDesktop;
